import { useEffect, useMemo, useState } from "react";
import "@styles/sections/BlogSection.scss";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  Dialog,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import GenericSwiper from "@components/ui/GenericSwiper";
import DropdownFilterMenu from "@components/ui/DropdownFilterMenu";
import { getBlogList } from "@api/landingApi";
import { getOptionData } from "@api/commonApi";
import {
  blogFilterData,
  genericLatestPopularFilterData,
} from "@data/FilterData";

import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";

const BlogSection = () => {
  const { t, i18n } = useTranslation();
  const isChinese = i18n.language === "hk";
  const isMobile = useMediaQuery("(max-width:425px)");

  const [slides, setSlides] = useState([]);
  const [optionsMap, setOptionsMap] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);



  // ====== 取 Blog List ======
  useEffect(() => {
    (async () => {
      try {
        const res = await getBlogList();
        setSlides(res.data?.Data || []);
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  // ====== 取所有 Filter Options（與 EventSection 同步的迴圈）======
  useEffect(() => {
    (async () => {
      const result = {};
      for (const f of blogFilterData) {
        if (f.staticOptions) {
          result[f.type] = f.staticOptions;
          continue;
        }
        try {
          const res = await getOptionData({ type: f.type });
          result[f.type] = res?.data?.Data || [];
        } catch (err) {
          console.error("Failed to fetch", f.type, err);
        }
      }
      setOptionsMap(result);
    })();
  }, []);

  // ====== Filter 選擇變更（與 EventSection 同風格）======
  const handleFilterChange = (type, selectedItem) => {
    const valueField =
      blogFilterData.find((f) => f.type === type)?.valueField || "value";
    setSelectedValues((prev) => ({
      ...prev,
      [type]: selectedItem?.[valueField],
    }));
  };

  // ====== Reset 全部 ======
  const handleResetAllFilters = () => {
    const init = {};
    blogFilterData.forEach((f) => (init[f.type] = ""));
    init.latest_most_popular = 0;
    setSelectedValues(init);
  };

  // ====== 過濾 + 排序（與 EventSection 相同邏輯，欄位映射照你原本）======
  const fieldMap = {
    blog_product: "product",
    blog_theme: "theme",
  };

  const filtered = useMemo(() => {
    // 1) 過濾
    const filteredCards = slides.filter((item) => {
      return Object.entries(selectedValues).every(([key, value]) => {
        if (!value) return true;
        if (key === "latest_most_popular") return true;

        const targetField = fieldMap[key];
        if (!targetField) return true;

        if (targetField === "product") {
          const itemThemes = (item.product?.split(",") || []).map((s) =>
            s.trim()
          );
          return itemThemes.includes(String(value));
        }
        if (targetField === "theme") {
          const itemThemes = (item.theme?.split(",") || []).map((s) => s.trim());
          return itemThemes.includes(String(value));
        }
        // 其他（例如 product）用等值匹配
        return String(item[targetField]) === String(value);
      });
    });

    // 2) 排序：0=最新 (id DESC)，1=最熱門 (view_no DESC)
    const sortValue = Number(selectedValues.latest_most_popular);
    const sorted = [...filteredCards].sort((a, b) => {
      if (sortValue === 1) {
        return (b.view_no || 0) - (a.view_no || 0);
      }
      return Number(b.id) - Number(a.id);
    });

    return sorted;
  }, [slides, selectedValues]);

  return (
    <Box className="blog-section-container">
      <Typography variant="h4" className="title">
        <Trans i18nKey="generic:blog" />
      </Typography>

      {isMobile ? (
        <>
          {/* ====== Mobile Filter Button ====== */}
          <Box className="dialog-mobile-filter-button-container">
            <Button
              className="dropdown-button mobile-filter-button"
              onClick={() => setMobileOpen(true)}
            >
              <FilterAltIcon />
              <Trans i18nKey="generic:filter" />
            </Button>
          </Box>

          {/* ====== Mobile Dialog：結構/行為貼齊 EventSection ====== */}
          <Dialog
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            fullScreen
            PaperProps={{ className: "dialog-filter-container" }}
            sx={{
              "& .MuiDialog-paper": {
                position: "fixed",
                bottom: 0,
                maxHeight: "50vh",
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                border: "2px solid #f5f5f5",
                background: "#fbfbfb",
              },
            }}
          >
            <Box className="dialog-top-container">
              <Typography className="dialog-title">
                {/* 修正：這裡應該是 blog，而不是 event */}
                <Trans i18nKey="generic:blog" />
              </Typography>
              <CloseIcon onClick={() => setMobileOpen(false)} />
            </Box>

            <Box className="dialog-content">
              {/* 排序：ToggleButtonGroup（同 EventSection） */}
              <Typography className="dialog-text">
                <SortIcon className="dialog-icon" />
                <Trans i18nKey="generic:sort" />
              </Typography>
              <Box className="toggle-button-container">
                <ToggleButtonGroup
                  value={selectedValues.latest_most_popular ?? 0}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleFilterChange("latest_most_popular", { value: newValue });
                    }
                  }}
                >
                  <ToggleButton value={0}>
                    <Trans i18nKey="generic:most_latest" />
                  </ToggleButton>
                  <ToggleButton value={1}>
                    <Trans i18nKey="generic:most_popular" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box className="dialog-line"></Box>

              {/* 篩選：Select 列表（同 EventSection 的 mobile 寫法） */}
              <Typography className="dialog-text">
                <FilterAltIcon className="dialog-icon" />
                <Trans i18nKey="generic:filter" />
              </Typography>

              {blogFilterData.map((filter) => {
                const langKey = filter.displayField?.[i18n.language];
                const valueField = filter.valueField || "value";
                const options = optionsMap[filter.type] || [];

                // 預設值：有 selectedValues 就用，否則取第一個 option 的 value
                const currentValue =
                  selectedValues?.[filter.type] ??
                  (options.length > 0 ? options[0][valueField] : "");

                return (
                  <Box className="select-container" key={filter.type}>
                    <Select
                      className="select-filter"
                      variant="outlined"
                      value={currentValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const selectedItem =
                          options.find(
                            (o) => String(o?.[valueField]) === String(v)
                          ) || null;
                        handleFilterChange(filter.type, selectedItem);
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: "20vh" } } }}
                    >
                      {options.map((opt) => (
                        <MenuItem key={opt[valueField]} value={opt[valueField]}>
                          {langKey
                            ? opt[langKey]
                            : opt.label || opt.name || String(opt[valueField])}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                );
              })}

              <Box className="dialog-line"></Box>

              {/* Reset / View results */}
              <Box className="dialog-button-container">
                <Button
                  className="button dialog-reset-filter-button"
                  onClick={handleResetAllFilters}
                >
                  <Trans i18nKey="generic:reset" />
                  <RestartAltIcon className="reset-icon" />
                </Button>
                <Button
                  className="loading-button"
                  onClick={() => setMobileOpen(false)}
                >
                  <Trans i18nKey="generic:view_results" />
                </Button>
              </Box>
            </Box>
          </Dialog>
        </>
      ) : (
        <>
          {/* ====== Desktop Filter Bar（與 EventSection 對齊） ====== */}
          <Box className="filter-container">
            {blogFilterData.map((filter) => (
              <DropdownFilterMenu
                key={filter.type}
                options={optionsMap[filter.type] || []}
                displayField={filter.displayField[i18n.language]}
                valueField={filter.valueField}
                lang={i18n.language}
                value={selectedValues[filter.type]}
                onChange={(item) => handleFilterChange(filter.type, item)}
              />
            ))}

            {/* 最新/最熱門 */}
            <DropdownFilterMenu
              options={genericLatestPopularFilterData}
              displayField={(item) =>
                isChinese ? item.desc_hk : item.desc_hk_en
              }
              valueField="value"
              lang={i18n.language}
              value={selectedValues.latest_most_popular}
              onChange={(item) =>
                handleFilterChange("latest_most_popular", item)
              }
              icon={"sort"}
            />

            {/* Reset */}
            <Button
              className="dropdown-button reset-filter-button"
              onClick={handleResetAllFilters}
            >
              <Trans i18nKey="generic:reset" />
              <RestartAltIcon className="reset-icon" />
            </Button>
          </Box>
        </>
      )}

      {/* ====== Swiper（保留你原本的展示邏輯） ====== */}
      {filtered.length > 0 ? (
        <GenericSwiper
          slides={filtered.map((s) => ({
            ...s,
            image: s.thumbnail_hk
              ? isChinese
                ? s.thumbnail_hk
                : s.thumbnail_hk_en
              : s.thumbnail || s.image,
            title: isChinese ? s.title_hk || s.title : s.title_hk_en || s.title,
            description: isChinese
              ? s.desc_hk || s.description
              : s.desc_hk_en || s.description,
              url: isChinese? s?.url_hk : s?.url_hk_en,
          }))}
          type="blog"
        />
      ) : (
        <Typography className="coming-soon-text" variant="h6">
          <Trans i18nKey="home:coming_soon" />
        </Typography>
      )}
    </Box>
  );
};

export default BlogSection;
